import { useEffect, useState } from 'react';

const TYPE_MS = 55;
const HOLD_MS = 2600;
const ERASE_MS = 22;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Types the joined lines out, holds, erases, repeats. Returns the full text
 * immediately when the visitor asked for reduced motion.
 */
export const useTypewriter = (lines: string[]): string[] => {
  const full = lines.join('\n');
  const [text, setText] = useState(() => (prefersReducedMotion() ? full : ''));

  useEffect(() => {
    if (prefersReducedMotion()) {
      setText(full);
      return;
    }

    let index = 0;
    let erasing = false;
    let timer: number;

    const tick = () => {
      // A background tab gets no animation: this loop runs forever, and on a
      // phone that is battery spent on something nobody is looking at.
      if (document.hidden) {
        timer = window.setTimeout(tick, 1000);
        return;
      }

      if (!erasing) {
        index += 1;
        setText(full.slice(0, index));
        if (index >= full.length) {
          erasing = true;
          timer = window.setTimeout(tick, HOLD_MS);
          return;
        }
        timer = window.setTimeout(tick, TYPE_MS);
        return;
      }

      index -= 1;
      setText(full.slice(0, index));
      if (index <= 0) {
        erasing = false;
        timer = window.setTimeout(tick, TYPE_MS * 6);
        return;
      }
      timer = window.setTimeout(tick, ERASE_MS);
    };

    timer = window.setTimeout(tick, 700);
    return () => window.clearTimeout(timer);
  }, [full]);

  return text.split('\n');
};
